// Config panel for "parallel" and "parallel_join" flow control blocks
import React from 'react';
import {
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const ParallelConfig = ({ config, onChange, onSummaryChange }) => {
  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    onSummaryChange(`${updated.branches || 2} branches`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Splits the flow into multiple branches that execute at the same time (concurrently).
      </Typography>

      <FormControl size="small" fullWidth>
        <InputLabel>Number of Branches</InputLabel>
        <Select
          value={config.branches || 2}
          onChange={(e) => handleChange('branches', e.target.value)}
          label="Number of Branches"
        >
          <MenuItem value={2}>2 branches</MenuItem>
          <MenuItem value={3}>3 branches</MenuItem>
          <MenuItem value={4}>4 branches</MenuItem>
        </Select>
      </FormControl>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>How it works:</strong><br />
          1. Connect each branch output to a separate chain of action blocks<br />
          2. All branches run at the same time<br />
          3. Use a <strong>Join (Wait All)</strong> block to reconverge — downstream nodes
          only execute after all branches finish
        </Typography>
      </Alert>

      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Shared variables:</strong> All branches share the same variables.
          If two branches write to the same variable, the last one to finish wins.
        </Typography>
      </Alert>
    </Box>
  );
};

const ParallelJoinConfig = ({ config, onChange, onSummaryChange }) => {
  // No config needed — just info
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Waits for all incoming parallel branches to complete before continuing downstream.
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Usage:</strong><br />
          Connect the end of each parallel branch to this node.
          The flow will only continue past this point when every
          incoming branch has finished executing.
        </Typography>
      </Alert>

      <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          This block has no configuration.
          Simply connect all parallel branch endpoints here,
          and connect the output to whatever should happen next.
        </Typography>
      </Alert>
    </Box>
  );
};

export { ParallelConfig, ParallelJoinConfig };
export default ParallelConfig;
